import {
  PENDING_CHUNK_RELOAD_KEY,
  PendingChunkReload,
  STALE_MODULE_RELOAD_KEY,
  StaleModuleReloadAttempt,
  nextStaleModuleReload,
  parseStaleModuleReloadAttempt,
  staleModuleReloadDelay,
} from 'shared/chunk-reload';

let reloadScheduled = false;

function readLastAttempt(): StaleModuleReloadAttempt | undefined {
  try {
    return parseStaleModuleReloadAttempt(sessionStorage.getItem(STALE_MODULE_RELOAD_KEY));
  } catch {
    return;
  }
}

function recordAttempt(attempt: StaleModuleReloadAttempt, pending: PendingChunkReload): boolean {
  try {
    const stored = JSON.stringify(attempt);
    sessionStorage.setItem(STALE_MODULE_RELOAD_KEY, stored);
    sessionStorage.setItem(PENDING_CHUNK_RELOAD_KEY, JSON.stringify(pending));
    return sessionStorage.getItem(STALE_MODULE_RELOAD_KEY) === stored;
  } catch {
    return false;
  }
}

export function takePendingChunkReload(): PendingChunkReload | undefined {
  try {
    const stored = sessionStorage.getItem(PENDING_CHUNK_RELOAD_KEY);
    if (!stored) {
      return;
    }
    sessionStorage.removeItem(PENDING_CHUNK_RELOAD_KEY);
    return JSON.parse(stored) as PendingChunkReload;
  } catch {
    return;
  }
}

export function isStaleModuleError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  if (err instanceof TypeError) {
    return /dynamically imported module|Importing a module script failed/i.test(err.message);
  }
  return /^Unable to preload CSS/i.test(err.message);
}

/**
 * True once this page has scheduled a reload, so errors from files that fail
 * to load in the meantime are expected and need no report.
 */
export function isStaleModuleReloadScheduled() {
  return reloadScheduled;
}

/**
 * Schedules a reload to pick up the current build, waiting longer on each
 * repeated failure of the same page (see STALE_MODULE_RELOAD_DELAYS_MS).
 * Returns false, and does not reload, when the page has used all its reloads
 * or the attempt can't be recorded (e.g. storage is blocked), so the caller
 * reports the error instead of reloading forever.
 */
export function attemptStaleModuleReload(context: string, err?: unknown): boolean {
  if (reloadScheduled) {
    return true;
  }
  const url = location.href;
  const now = Date.now();
  const attempt = nextStaleModuleReload(readLastAttempt(), url, now);
  if (!attempt) {
    return false;
  }
  const recorded = recordAttempt(attempt, {
    context,
    at: now,
    url,
    attempt: attempt.count,
    message: err instanceof Error ? err.message : undefined,
  });
  if (!recorded) {
    return false;
  }
  reloadScheduled = true;
  setTimeout(() => location.reload(), staleModuleReloadDelay(attempt));
  return true;
}
